/**
 * @module app.ag
 */

import { bindable } from 'aurelia-typed-observable-plugin';
import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { CaseService } from 'app/ag/services/case-service';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { openClaimSummary } from 'app/cd/components/summary/claim/claim-summary';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions'
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import * as AG from 'app/ag/models/essette-api';

@autoinject()
export class MemberAgCases extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @bindable
    protected showCaseGoButton: boolean = false;
    protected hasCaseRoute: boolean = true;

    public memberAGCases: ReadonlyArray<AG.Case> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected openProviderSummary = openProviderSummary;
    protected openClaimSummary = openClaimSummary;
    protected showProviderSummaryLink: boolean = false;

    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);

        // Custom Sort
        this.sorting.entries = [
            {
                reference: 'dueDateTime',
                direction: SortDirection.Ascending
            }
        ];

        this.checkPermissions();
    }

    protected bind(): void {
        this.loadCustomFieldTargets();
        this.loadMemberAGCases();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadMemberAGCases();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'dueDateTime',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ***********************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    ////****************** Service Calls ********************************************////
    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('AG.Case', { definitionCount: 25 });
    }

    @apiCall
    private async loadMemberAGCases(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes = [
            'assignedTo',
            'categories',
            'custom',
            'member',
            'priority',
            'provider',
            'provider.location',
            'requested.method',
            'requested.source',
            'status',
            'status.reason',
            'subCategories',
            'subCategories.category',
            'type'
        ];

        const fields = [
            'assignedTo.description',
            'categories.description',
            'custom.lookup.*',
            'custom.text.*',
            'dueDateTime',
            'member.name.full',
            'number',
            'priority.description',
            'provider.description',
            'provider.location.description',
            'receivedTimestamp',
            'requested.method.description',
            'requested.source.description',
            'status.description',
            'status.reason.description',
            'subCategories.description',
            'subCategories.category.description',
            'type.description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadCasesByMember(this.memberId, options, includes, fields);
            this.memberAGCases = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
